import {
  BuildingOfficeIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import { EllipsisHorizontalCircleIcon } from "@heroicons/react/24/outline";
import { ICustomNode } from "../types";
import "../views/OfficeHierarchyOrgChart/css/OHOrgChartNode.css";
import CSS from "csstype";

export default function PDFOrgChartNode(props: { data: ICustomNode }) {
  const { data } = props;

  const determineColors = (
    areaType: string,
    districtType: string,
    territoryType: string,
    parentId?: string,
    parentIds?: string
  ) => {
    if (!parentId && !parentIds) {
      return { backgroundColor: "#ECF9FF", textColor: "#203A75" }; // Color for Chief Sales Officer
    } else if (territoryType == "Territory") {
      return { backgroundColor: "#ECF9FF", textColor: "#0C77BD" }; // Color for President, Sales
    } else if (areaType == "Area") {
      return { backgroundColor: "#ECF9FF", textColor: "#069EDB" }; // Color for VP, Sales
    } else if (districtType == "District") {
      return { backgroundColor: "#203A75", textColor: "#FFF" }; // Color for District Manager
    }
    // Default color for other titles
    return { backgroundColor: "#fff", textColor: "#000" }; // You can adjust the default text color
  };

  const { backgroundColor, textColor } = determineColors(
    data.data.areaType,
    data.data.districtType,
    data.data.territoryType,
    data.data.parentId,
    data.data.parentIds
  );

  //#region Style for download org chart
  const position: CSS.Properties = {
    boxShadow:
      "0px 1px 3px 0px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 2px 1px -1px rgb(0 0 0 / 12%)",
    borderRadius: "20px",
    cursor: "default",
    display: "flex",
    flexDirection: "column",
    padding: "10px",
    paddingTop: "0",

    backgroundColor:
      !data.data.parentId && !data.data.parentIds
        ? "#203a75"
        : data.data.territoryType === "Territory"
        ? "#0C77BD"
        : data.data.areaType === "Area"
        ? "#069EDB"
        : data.data.districtType === "District"
        ? "#ECF9FF"
        : data.data.type === "Office"
        ? "#fff"
        : "",
    paddingBottom:
      (!data.data.parentId && !data.data.parentIds) ||
      data.data.districtType === "District"
        ? "22px"
        : data.data.type === "Office"
        ? "0"
        : "10px",

    border: data.data.type === "Office" ? "1px solid #203A75" : undefined,
    maxHeight: data.data.type === "Office" ? "500px" : undefined,
  };

  const chiefNameEllipsis: CSS.Properties = {
    border: "none",
    overflow: "hidden",
    outline: "none",
    margin: "5px",
    lineHeight: "30px",
    width: "95%",
    borderRadius: "10px",
    display: "flex",
    backgroundColor: backgroundColor,
    color: textColor,
    marginLeft: "-15px",
    marginTop: "22px",
  };

  const districtCard: CSS.Properties = {
    border: "none",
    overflow: "hidden",
    outline: "none",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginLeft: "40px",
  };
  const assocCard: CSS.Properties = {
    border: "none",
    overflow: "hidden",
    outline: "none",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginLeft: "-10px",
    marginTop: "-24px",
    width: "100%",
  };

  const cardEllipsis: CSS.Properties = {
    right: "45px",
    position: "absolute",
    top: "14px",
    width: "25px",
    marginTop: "12px",
  };

  const user: CSS.Properties = {
    borderRadius: "9999px",
    width: "2rem",
    height: "2rem",
    marginLeft: "-16px",
    color: "#ECF9FF",
  };

  const iconUser: CSS.Properties = {
    backgroundColor:
      !data.data.parentId && !data.data.parentIds ? "#203A75" : textColor,
    alignSelf: "left",
    right: "270px",
    position: "absolute",
    top: "16px",
    width: "0px",
    borderWidth: "8px",
    borderColor: "#ECF9FF",
    borderRadius: "9999px",
    borderStyle: "solid",
    padding: "1.5rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    height: "33px",
    boxSizing: "unset",
  };

  const districtIconUser: CSS.Properties = {
    alignSelf: "left",
    right: "270px",
    position: "absolute",
    top: "16px",
    width: "0px",
    borderWidth: "8px",
    borderColor: "#ECF9FF",
    backgroundColor: "#203A75",
    borderRadius: "9999px",
    borderStyle: "solid",
    padding: "1.5rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    height: "33px",
    boxSizing: "unset",
  };

  const iconOffice: CSS.Properties = {
    alignSelf: "left",
    right: "270px",
    position: "absolute",
    top: "-28px",
    width: "0px",
    borderWidth: "8px",
    borderColor: "#ECF9FF",
    backgroundColor: "#203A75",
    borderRadius: "9999px",
    borderStyle: "solid",
    padding: "1.5rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    height: "33px",
    boxSizing: "unset",
  };

  const cardName: CSS.Properties = {
    border: "none",
    overflow: "hidden",
    outline: "none",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginLeft: "35px",
  };

  const cardNameDistrictH3: CSS.Properties = {
    fontSize: "18px",
    cursor: "pointer",
    fontWeight: "bold",
    border: "none",
    textAlign: "center",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    margin: "0",
    color: "white",
  };

  const cardType: CSS.Properties = {
    height: "auto",
    color: data.data.districtType === "District" ? "#203A75" : "#fff",
    borderRadius: "30px",
    fontSize: "14px",
    fontWeight: "bold",
    marginLeft: data.data.districtType === "District" ? "22px" : "18px",
    alignSelf: "start",
    display: "flex",
    justifyContent: "left",
    padding: "1px",
    minWidth: "70px",
  };

  const districtCode: CSS.Properties = {
    alignSelf: "left",
    display: "flex",
    padding: "7px",
    marginLeft: "16px",
    minWidth: "70px",
    height: "auto",
    color: "#203A75",
    borderRadius: "30px",
    fontSize: "14px",
    fontWeight: "bold",
    textDecorationLine: "underline",
  };

  const districtLabel: CSS.Properties = {
    textDecorationLine: "underline",
    display: "flex",
    justifyContent: "space-between",
    padding: "10px",
    color: "#203a75",
    fontSize: "13px",
  };

  const districtLabelNone: CSS.Properties = {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px",
    color: "#203a75",
    fontSize: "13px",
  };

  const code: CSS.Properties = {
    textDecorationLine: "underline",
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px",
    marginTop: "-34px",
    cursor: "pointer",
    color: "#203a75",
    fontSize: "13px",
  };

  const officeHierarchy: CSS.Properties = {
    display: "flex",
    padding: "12px",
    paddingLeft: "18px",
    paddingTop: "3px",
    minWidth: "70px",
    height: "auto",
    color: "#fff",
    borderRadius: "30px",
    fontSize: "14px",
  };

  const ellipsis: CSS.Properties = {
    borderRadius: "9999px",
    width: "1.5rem",
    height: "1.5rem",
  };

  const cardAssociatedOffice: CSS.Properties = {
    fontSize: "18px",
    cursor: "pointer",
    fontWeight: "bold",
    border: "none",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    padding: "15px",
    paddingLeft: "35px",
    backgroundColor: "#203a75",
    borderRadius: "20px",
    color: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const cardAssociatedOfficeName: CSS.Properties = {
    fontSize: "12px",
    cursor: "pointer",
    border: "none",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    margin: "0",
    marginBottom: "10px",
    textDecorationLine: "underline",
    opacity: 1,
    color: " rgb(12 119 189)",
  };

  const cardAssocOfficeName: CSS.Properties = {
    border: "none",
    overflow: "hidden",
    outline: "none",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: "20px",
    marginLeft:
      data.data?.associatedOffices && data.data?.associatedOffices?.length > 18
        ? "20px"
        : "10px",
    overflowY:
      data.data?.associatedOffices && data.data?.associatedOffices?.length > 18
        ? "scroll"
        : undefined,
  };

  const cardNameH3: CSS.Properties = {
    fontSize: "18px",
    cursor: "pointer",
    fontWeight: "bold",
    border: "none",
    textAlign: "center",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    margin: "0",
    textDecoration: "underline",
  };

  const employeeID: CSS.Properties = {
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  };

  const districtBody: CSS.Properties = {
    opacity: "1",
    backgroundColor: "rgb(255 255 255 / var(--tw-bg-opacity))",
    borderRadius: "0.75rem",
    marginLeft: "0.5rem",
    marginRight: "1rem",
  };

  //#endregion
  return (
    <div style={position}>
      {data.data.districtType === "District" ? (
        data.data.districtManager !== null ? (
          <div style={chiefNameEllipsis}>
            <div style={districtCard}>
              <div>
                <h3 style={cardNameDistrictH3}>{data.data.districtManager}</h3>
              </div>
              <div>
                {data.data.employeeNumber !== "" ? data.data.employeeNumber : 0}
              </div>
            </div>
            <div style={cardEllipsis}>
              <EllipsisHorizontalCircleIcon style={ellipsis} />
            </div>
            <div style={districtIconUser}>
              <UsersIcon style={user} />
            </div>
          </div>
        ) : (
          ""
        )
      ) : data.data.type === "Office" ? (
        <>
          <div>
            <div style={assocCard}>
              <h3 style={cardAssociatedOffice}>Associated Offices</h3>

              <div style={iconOffice}>
                <BuildingOfficeIcon style={user} />
              </div>
            </div>
          </div>
          <div style={cardAssocOfficeName}>
            <div>
              {data.data.associatedOffices.map((element, index) => (
                <h3 key={index} style={cardAssociatedOfficeName}>
                  {element.officeName}
                </h3>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={chiefNameEllipsis}>
            <div style={cardName}>
              <div>
                <h3 style={cardNameH3}>{data.data.fullName} </h3>
              </div>
              <div style={employeeID}>
                {data.data.employeeNumber !== "" ? data.data.employeeNumber : 0}
              </div>
            </div>
            <div>
              <div style={cardEllipsis}>
                <EllipsisHorizontalCircleIcon style={ellipsis} />
              </div>
              <div style={iconUser}>
                <UserIcon style={user} />
              </div>
            </div>
          </div>
        </>
      )}
      <div style={cardType}>
        {data.data.territoryType === "Territory"
          ? data.data.territoryManagerTitle
          : data.data.areaType === "Area"
          ? data.data.areaManagerTitle
          : data.data.districtManagerTitle}
      </div>

      {data.data.districtType === "District" ? (
        <div style={districtCode}>
          {data.data.districtType === "District" ? data.data.districtCode : ""}
        </div>
      ) : (
        ""
      )}
      {(data.data.parentId !== "" && data.data.parentIds !== "") ||
      (data.data.parentId == "" && data.data.parentIds !== "") ? (
        <>
          {data.data.districtType === "District" ? (
            <div style={districtBody}>
              <div>
                {data.data.regionManager !== null ? (
                  <label style={districtLabel}>{data.data.regionManager}</label>
                ) : (
                  <label style={districtLabelNone}>Region</label>
                )}
                <span style={code}>{data.data.regionCode}</span>
              </div>
              <div>
                {data.data.areaManager !== null ? (
                  <label style={districtLabel}>{data.data.areaManager}</label>
                ) : (
                  <label style={districtLabelNone}>Area</label>
                )}
                <span style={code}>{data.data.areaCode}</span>
              </div>
              <div>
                {data.data.territoryManager !== null ? (
                  <label style={districtLabel}>
                    {data.data.territoryManager}
                  </label>
                ) : (
                  <label style={districtLabelNone}> Territory</label>
                )}
                <span style={code}>{data.data.territoryCode}</span>
              </div>
            </div>
          ) : (
            <div style={officeHierarchy}>
              {!data.data.parentId && !data.data.parentIds
                ? ""
                : data.data.territoryType === "Territory"
                ? data.data.territoryCode.replace("-", "") +
                  " - " +
                  data.data.name
                : data.data.areaType === "Area"
                ? data.data.areaCode.replace("-", "") +
                  " - " +
                  data.data.areaName
                : ""}
            </div>
          )}
        </>
      ) : (
        ""
      )}
    </div>
  );
}
